/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import * as _ from 'lodash';
import {map, Observable, throwError} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';
import {LineItemEditApi, LineItemModel} from './line-item-api.service';
import {transformLineItemRequest, transformLineItemResponse} from './line-item-api-transforms';

@Injectable({providedIn: 'root'})
export class LineItemTemplateApiService extends SignalAdminResourceApiBase<LineItemModel> implements LineItemEditApi {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'lineitemtemplates', {showBudgetSpend: true}, () => localUserProfile?.isReadOnly() || false);
  }

  override get(id: string): Observable<LineItemModel> {
    return super.get(id).pipe(map(transformLineItemResponse));
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<LineItemModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map(transformLineItemResponse)
    })));
  }

  override save(model: LineItemModel): Observable<LineItemModel> {
    return this.saveToEndpoint(transformLineItemRequest(model), '');
  }

  protected override persistPartial(payload: LineItemModel, endpoint: string): Observable<LineItemModel> {
    return this.saveToEndpoint(transformLineItemRequest(payload), endpoint);
  }

  bulkSave(params: Record<string, any>, models: Partial<LineItemModel>[]): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri('lineitemtemplates'),
      params: {showBudgetSpend: true, ...params},
      body: models
    });
  }

  dimensionsCheck(id: string, limitToRequestedInLastDay = true): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri(['lineitemtemplates', id, 'dimensionsCheck']),
      params: {showBudgetSpend: true, limitToRequestedInLastDay}
    }).pipe(map((response) => response || {}));
  }

  getPublisherApprovals(id: string): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri(['lineitemtemplates', id, 'publisherapprovals']),
      params: {showBudgetSpend: true}
    });
  }

  copyTemplate(model: LineItemModel, newId: string, copyTargeting = true, asLineItem = false): Observable<void> {
    if (!model?.id) {
      return throwError(() => ({data: {errors: [{error: {text: 'Insufficient parameters for the line item template copy'}}]}}));
    }
    const body = {
      id: model.id,
      orderId: model.order?.id || '',
      lineItemId: newId || '',
      name: model.name,
      copyTargeting,
      userState: model.userState || '',
      ...(asLineItem ? {startDate: model.startDate, endDate: model.endDate} : {})
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['lineitemtemplates', model.id, asLineItem ? 'lineitem' : 'copy']),
      params: {showBudgetSpend: true, ..._.omit(body, 'id')},
      body
    });
  }

  private saveToEndpoint(payload: LineItemModel, endpoint: string): Observable<LineItemModel> {
    const path = endpoint
      ? ['lineitemtemplates', payload.id, endpoint]
      : ['lineitemtemplates', payload.id];
    const params = _.pick(payload, [
      'concise',
      'ignoreProduct',
      'updateOnlyTheseTargetTypes',
      'skipTeamBasedTargeting',
      'skipTeamBasedTargetingType'
    ]);
    return this.client.request<LineItemModel>({
      method: 'POST',
      url: this.resourceUri(path),
      params: {showBudgetSpend: true, ...params},
      body: payload
    }).pipe(map((saved) => transformLineItemResponse({...payload, ...(saved || {})})));
  }
}

export function lineItemEditApiForModel(
  model: any,
  lineItemApi: LineItemEditApi,
  lineItemTemplateApi: LineItemEditApi
): LineItemEditApi {
  return model?.resourceName === 'lineItemTemplate' ? lineItemTemplateApi : lineItemApi;
}
